import { useQuery } from '@apollo/client';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { EndiannessEnum, StatusEnum, SummationTypeEnum, ValueTypeEnum } from '../interfaces/enums';
import { intervalFrequency, intervalMins, unitToSummationType } from './constants';

const FIRST_NAME_PART_INDEX = 0;
const LAST_NAME_PART_OFFSET = 1;
const SINGLE_NAME_PART_COUNT = 1;

export const numberWithCommas = (x: number) => {
  const parts = x.toString().split('.');
  // eslint-disable-next-line
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

export const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const uppercaseFirstLetter = (str: string) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1);
  });
};

export const capitalize = (str = '') => str.charAt(0).toUpperCase() + str.substring(1);

export const groupBy = <T, K extends keyof any>(list: T[], getKey: (item: T) => K) =>
  list.reduce((previous, currentItem) => {
    const group = getKey(currentItem);
    if (!previous[group]) previous[group] = [];
    previous[group].push(currentItem);
    return previous;
  }, {} as Record<K, T[]>);

export const getParameterByName = (name: string) => {
  const theUrl = window.location.href;
  // eslint-disable-next-line
  const theName = name.replace(/[\[\]]/g, '\\$&');
  // eslint-disable-next-line
  const regex = new RegExp('[?&]' + theName + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(theUrl);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const convertQueryStringToJSON = () => {
  const pairs = window.location.search.slice(1).split('&');

  const result = {};
  pairs.forEach(function (pair) {
    const newPair = pair.split('=');
    result[newPair[0]] = decodeURIComponent(newPair[1] || '');
  });
  return JSON.parse(JSON.stringify(result));
};

export const convertOrderDotNotationToObject = (field: string, direction: string) => {
  let orderBy = {};
  if (field) {
    const data = [{ key: field, value: direction }];
    orderBy = data.reduce(function (r, o) {
      const path = (o.key as string).split('.'),
        last = path.pop();

      path.reduce(function (p, k) {
        return (p[k] = p[k] || {});
      }, r)[last] = o.value;
      return r;
    }, {});
  }
  return orderBy;
};

export const getInitials = (name: string) => {
  const nameParts = name?.trim().split(/\s+/).filter(Boolean) ?? [];

  if (nameParts.length === 0) {
    return '';
  }

  if (nameParts.length === SINGLE_NAME_PART_COUNT) {
    return nameParts[FIRST_NAME_PART_INDEX].charAt(0).toUpperCase();
  }

  const lastNamePartIndex = nameParts.length - LAST_NAME_PART_OFFSET;

  return `${nameParts[FIRST_NAME_PART_INDEX].charAt(0)}${nameParts[lastNamePartIndex].charAt(0)}`.toUpperCase();
};

export const mapChannelOptionsToChannels = (
  channelOptions: any,
  namePrefix: string,
  LoadId: string,
  Feed?: {
    FeedId?: string;
    Label?: string;
    FeedsType?: string;
  }
) => {
  const newChannels = channelOptions.map((x) => {
    const channelName = `${namePrefix} ${x.FullData?.Description}`;
    const uom = x.FullData?.Value || '';
    const sumType = unitToSummationType[uom];

    const newChannel = {
      ChannelId: uuidv4(),
      LoadId: LoadId,
      ChannelKey: x.ChannelKey ? x.ChannelKey : uniqueChannelKey(),
      Label: channelName,
      UnitOfMeasure: x.FullData?.Value,
      IntervalFrequency: '15 minute',
      IntervalMins: 15,
      TimeZone: 'NZST/NZDT',
      Status: StatusEnum.Active,
      FeedId: Feed?.FeedId,
      EnableAlerts: x.FullData?.Label.indexOf('kWh') > -1 ? true : false,
      StartDate: moment.utc().format('YYYY-MM-DDTHH:mm:00'),
      CreatedOn: moment.utc().format('YYYY-MM-DDTHH:mm:00'),
      UpdatedOn: moment.utc().format('YYYY-MM-DDTHH:mm:00'),
      ValueTypeEnum: ValueTypeEnum.ActualForInterval,
      SummationType: sumType && SummationTypeEnum[sumType] ? SummationTypeEnum[sumType] : 1,
    };
    return newChannel;
  });
  return newChannels;
};

export const mapChannelsToFeedsConfigModbusChannels = (channels: any) => {
  return channels.map((channel) => {
    const paramSplit = channel.Parameter?.split('/') || [];
    let deviceAddress = paramSplit.length > 0 ? Number(paramSplit[0]) : 0;
    let registerAddress = paramSplit.length > 1 ? Number(paramSplit[1]) : 0;
    if (isNaN(deviceAddress)) {
      deviceAddress = 0;
    }
    if (isNaN(registerAddress)) {
      registerAddress = 0;
    }
    const frequency = channel.IntervalFrequency || '15 minute';
    const frequencyIdx = intervalFrequency.indexOf(frequency);
    const frequencyVal = intervalMins[frequencyIdx > -1 ? frequencyIdx : 1];
    const frequencySec = frequencyVal * 60;
    return {
      ChannelId: channel.ChannelId,
      FeedId: channel.FeedId || channel.Feed?.FeedId,
      DeviceAddress: deviceAddress,
      ReadFrequencySeconds: frequencySec || 15 * 60,
      RegisterAddress: registerAddress,
      RegisterCount: 1,
      RegisterType: 0,
      Endianness: EndiannessEnum.Big,
      RtuBaudRate: 9600,
      RtuDataBits: 8,
      RtuParity: 'none',
      RtuSerialPort: 'dev/ttyAMA0',
      RtuStopBits: 'One',
      ScalingFactor: 0,
      TcpIpAddress: null,
      TcpPort: null,
      Status: 1,
      CalculateDelta: false,
      // some to be false
      IsBigEndian: true,
      CreatedOn: moment.utc().format('YYYY-MM-DDTHH:mm:00'),
      UpdatedOn: moment.utc().format('YYYY-MM-DDTHH:mm:00'),
    };
  });
};

export const toCamel = (o: any) => {
  let newO, origKey, newKey, value;

  if (o instanceof Array) {
    return o.map(function (value) {
      if (typeof value === 'object') {
        value = toCamel(value);
      }
      return value;
    });
  } else {
    newO = {};
    for (origKey in o) {
      if (o.hasOwnProperty(origKey)) {
        newKey = (origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey).toString();
        value = o[origKey];
        if (value instanceof Array || (value !== null && value.constructor === Object)) {
          value = toCamel(value);
        }
        newO[newKey] = value;
      }
    }
  }
  return newO;
};

export const uniqueChannelKey = () => {
  return Math.random().toString(36).substr(2, 7).toUpperCase() + Date.now() + 'ESP';
};

export const ObjByString = (object: any, string: string) => {
  string = string.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  string = string.replace(/^\./, ''); // strip a leading dot
  const array = string.split('.');
  for (let i = 0, n = array.length; i < n; ++i) {
    const key = array[i];
    if (key in object) {
      object = object[key];
    } else {
      return;
    }
  }
  return object;
};

export const setCookie = (cname: string, cvalue: string) => {
  const date = new Date();
  date.setTime(date.getTime() + 60 * 60 * 1000);
  let expires = 'expires=' + date.toUTCString();
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
};

export const getCookie = (cname: string) => {
  let name = cname + '=';
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

export const inIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

export const getLowestIntervalFromLoads = (loads: any) => {
  const intervals = loads.flatMap((x) => x.Channels.flatMap((y) => y.IntervalFrequency));
  let lowestIntervalIdx = 0;
  intervals.forEach((x) => {
    const idx = intervalFrequency.indexOf(x);
    if (idx > lowestIntervalIdx) {
      lowestIntervalIdx = idx;
    }
  });
  const frequency = intervalFrequency[lowestIntervalIdx];
  return frequency;
};

export const useImperativeQuery = (query) => {
  const { refetch } = useQuery(query, { skip: true });

  const imperativelyCallQuery = (variables) => {
    return refetch(variables);
  };

  return imperativelyCallQuery;
};

export const isString = (val: any) => typeof val === 'string' || val instanceof String;

export const splitPascalCaseLabel = (value: string) => value.replace(/([a-z])([A-Z])/g, '$1 $2');

const MACRON_VOWEL_GROUPS: Record<string, string> = {
  a: 'aā',
  ā: 'aā',
  e: 'eē',
  ē: 'eē',
  i: 'iī',
  ī: 'iī',
  o: 'oō',
  ō: 'oō',
  u: 'uū',
  ū: 'uū',
};

const REGEX_SPECIAL_CHARS = new Set(['.', '*', '+', '?', '^', '$', '{', '}', '(', ')', '|', '[', ']', '\\']);

// Builds a Postgres/Hasura `_iregex`-compatible pattern where each vowel is expanded to a
// character class covering both its macron and non-macron forms (e.g. "kowhai" -> "k[oō]wh[aā]i"),
// so users can find macron names (e.g. "Kōwhai") without typing the macron.
export const buildMacronInsensitiveSearchPattern = (term: string): string => {
  const normalized = (term ?? '').normalize('NFC').toLowerCase();
  return Array.from(normalized)
    .map((char) => {
      const vowelGroup = MACRON_VOWEL_GROUPS[char];
      if (vowelGroup) {
        return `[${vowelGroup}]`;
      }
      return REGEX_SPECIAL_CHARS.has(char) ? `\\${char}` : char;
    })
    .join('');
};
